/**
 * TentpoleDirectory — collapsible catalog of every tentpole (including ones
 * with no plans yet), grouped by quarter. With ~80 seeded moments the plan
 * sections only render tentpoles that have plans; this table is where the
 * gaps live — and where Edit / + Add plan stay reachable for empty tentpoles.
 * Like the section headers, it labels only the exceptions (Inactive,
 * External, Happening now); internal is every tentpole's default.
 */

import React, { useState } from 'react';
import { Button, Card, StatusPill, tableStyles } from '../../ui';
import WidgetHeader from '../WidgetHeader';
import TimingPill from './TimingPill';
import { Tentpole, TentpoleCategory, TENTPOLE_CATEGORY_LABELS } from './types';
import { fmtTentpoleWindow, isUpcomingTentpole, quarterLabel, tentpoleLabel, tentpoleTiming } from './shared';

interface TentpoleDirectoryProps {
  tentpoles: Tentpole[];
  /** slug → count of currently-filtered entries. */
  entryCounts: Record<string, number>;
  timeframe: 'upcoming' | 'past' | 'all';
  category: '' | TentpoleCategory;
  canManage: boolean;
  onEditTentpole: (t: Tentpole) => void;
  onAddEntryFor: (slug: string) => void;
}

const TentpoleDirectory: React.FC<TentpoleDirectoryProps> = ({
  tentpoles,
  entryCounts,
  timeframe,
  category,
  canManage,
  onEditTentpole,
  onAddEntryFor,
}) => {
  const rows = tentpoles
    .filter((t) => {
      if (timeframe === 'upcoming' && !isUpcomingTentpole(t)) return false;
      if (timeframe === 'past' && isUpcomingTentpole(t)) return false;
      if (category && t.category !== category) return false;
      return true;
    })
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  // First run (no entries anywhere) the directory IS the content — start open.
  const anyEntries = tentpoles.some((t) => (entryCounts[t.slug] ?? 0) > 0);
  const [open, setOpen] = useState(!anyEntries);

  const subtitle = `${rows.length} ${timeframe === 'all' ? '' : `${timeframe} `}${rows.length === 1 ? 'tentpole' : 'tentpoles'}, planned or not`;
  const colSpan = canManage ? 6 : 5;

  let lastQuarter = '';
  const bodyRows: React.ReactNode[] = [];
  rows.forEach((t) => {
    const quarter = quarterLabel(t.startDate);
    if (quarter !== lastQuarter) {
      lastQuarter = quarter;
      bodyRows.push(
        <tr key={`q-${quarter}`}>
          <td colSpan={colSpan} style={quarterBandStyle}>{quarter}</td>
        </tr>,
      );
    }
    const timing = tentpoleTiming(t);
    bodyRows.push(
      <tr key={t.slug}>
        <td style={tableStyles.td}>
          <span style={{ fontWeight: 600 }}>
            {tentpoleLabel(t)}
          </span>
          {!t.active && (
            <StatusPill variant="paused" size="sm" noDot style={{ marginLeft: 8 }}>Inactive</StatusPill>
          )}
          {t.externalVisible && (
            <StatusPill variant="approved" size="sm" noDot style={{ marginLeft: 8 }}>External</StatusPill>
          )}
        </td>
        <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>{fmtTentpoleWindow(t)}</td>
        <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
          {timing.phase === 'live'
            ? <TimingPill tentpole={t} />
            : <span style={{ color: timing.phase === 'past' ? 'var(--fg-3)' : undefined }}>{timing.label}</span>}
        </td>
        <td style={tableStyles.td}>{TENTPOLE_CATEGORY_LABELS[t.category]}</td>
        <td
          style={{
            ...tableStyles.tdNum,
            color: entryCounts[t.slug] ? 'var(--fg-1)' : 'var(--fg-3)',
            fontWeight: entryCounts[t.slug] ? 600 : 400,
          }}
        >
          {entryCounts[t.slug] ?? 0}
        </td>
        {canManage && (
          <td style={{ ...tableStyles.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
            <Button size="sm" variant="ghost" onClick={() => onEditTentpole(t)}>Edit</Button>
            <Button size="sm" variant="subtle" onClick={() => onAddEntryFor(t.slug)} style={{ marginLeft: 6 }}>
              + Add plan
            </Button>
          </td>
        )}
      </tr>,
    );
  });

  return (
    <Card>
      <WidgetHeader
        title="Tentpole directory"
        subtitle={subtitle}
        right={
          <Button size="sm" variant="subtle" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : `Show all ${rows.length}`}
          </Button>
        }
      />
      {open && (
        rows.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', padding: '14px 0 2px' }}>
            No tentpoles in this timeframe.
          </div>
        ) : (
          <div style={{ ...tableStyles.wrapScroll, marginTop: 12 }}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Moment</th>
                  <th style={tableStyles.th}>Window</th>
                  <th style={tableStyles.th}>Timing</th>
                  <th style={tableStyles.th}>Category</th>
                  <th style={tableStyles.thNum}>Plans</th>
                  {canManage && <th style={tableStyles.th} aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>{bodyRows}</tbody>
            </table>
          </div>
        )
      )}
    </Card>
  );
};

const quarterBandStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  background: 'var(--cg-neutral-50)',
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-subtle)',
};

export default TentpoleDirectory;
